/* GameEditorComponent.js */

"use strict";
const LayoutManager = require("@gluenetworks/layout_manager");
const ModalUserFeedback = require("@gluenetworks/steve_ui_widgets").UserFeedback.ModalUserFeedback;
const DefaultMessageViewComponent = require("./utils/gameeditormessagecomponent/GameEditorMessageComponent");
const Renderer = require("./GameEditorRenderer");
const _ = require("lodash");
const MAX_ATTEMPTS = 10;

/*
 NOTE: The GameEditorComponent acts as a facade to directly control its sub-components (through the renderer).
 Generally, this is a *really bad* idea. In this case, the component pattern is still being used because
 the editor-layout will expect an api matching the component pattern, and it must still layout & cleanup dom (done in the renderer)

 In other words.... don't try this at home.
 */

class GameEditorComponent extends LayoutManager {
    constructor(logger, di, editorState, editorLayoutMgr) {
        super();
        this._logger = logger;
        this._registeredViews = [];
        this._registeredActions = [];
        this._registeredViewActions = [];
        this._activeView = null;
        this._modalUserFeedback = new ModalUserFeedback();
        this._editorLayoutMgr = editorLayoutMgr;
        this._editorState = editorState || {};
        this._renderer = new Renderer(this);

        this._defaultDiOpts = null;
        this._stateAttempt = 1;

        this._di = di.createDependencyInjector();
        this._storageFactory = this._di.getRegistered("$teve.storageFactory");
        this._LMRBLayout = this._di.getRegistered("EditorLayout.LMRBLayout");
        this._solutionManager = this._di.getRegistered("$teve.solutionsManager");

        this._init();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _init() {
        this._di.registry.register("$teve.ui", "userFeedback", this._modalUserFeedback);

        //----quick solution for standalone work----//
        //----game_center
        if (this._storageFactory && this._LMRBLayout && this._solutionManager) {
            this._storageFactory = this._storageFactory.registeredObj;
            this._LMRBLayout = this._LMRBLayout.registeredObj;
            this._solutionManager = this._solutionManager.registeredObj;
        } else {
            //-----other environment
            this._gameEditorData = true;
            this._storageFactory = {};
            this._LMRBLayout = {};
            this._solutionManager = {};
            this.logger.warn(
                "GameEditor._init() warn: not all components were registered successfully, some functionality will be turned off"
            );
        }
        //------------------------------------------////

        this._defaultDiOpts = {
            defaultInjectables: ["$teve.logger", "gameEditor.gameEditor"],
        };

        this._registerListeners();
    }

    _registerListeners() {
        this.on(
            "EditorTabActionIconClicked",
            (this._onEditorTabActionIconClicked = this._onEditorTabActionIconClicked.bind(this))
        );
        this._cabinetCmpnt.on("SelectView", (this._onSelectView = this._onSelectView.bind(this)));
        this._cabinetCmpnt.on("SelectAction", (this.runAction = this.runAction.bind(this)));
        this._breadCrumbCmpnt.on(
            "ContentClicked",
            (this._onBreadCrumbContentClicked = this._onBreadCrumbContentClicked.bind(this))
        );
        this._viewActionsCmpnt.on(
            "RequestViewAction",
            (this.runViewAction = this.runViewAction.bind(this))
        );
        this._titleCmpnt.on(
            "actionIconClicked",
            (this._onViewHeaderActionIconClicked = this._onViewHeaderActionIconClicked.bind(this))
        );
    }
    _unregisterListeners() {
        this._cabinetCmpnt.removeAllListeners("SelectView");
        this._cabinetCmpnt.removeAllListeners("SelectAction");
        this._breadCrumbCmpnt.removeAllListeners("ContentClicked");
        this._viewActionsCmpnt.removeAllListeners("RequestViewAction");
        this._titleCmpnt.removeAllListeners("DescriptionIconClicked");
        this.removeAllListeners("EditorTabActionIconClicked");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    get logger() {
        return this._logger;
    }

    get gameEditorData() {
        return this._gameEditorData;
    }

    get currentSolutionName() {
        return _.get(this._solutionManager, "_activeSolution.name");
    }

    closeEditor(id, force) {
        if (force) {
            this.onBeforeUnload = null;
        }
        this._editorLayoutMgr.closeEditor(id);
    }
    shutdown() {
        this._unregisterListeners();
        let prom = Promise.resolve();
        if (this._activeView) {
            prom = this._activeView.shutdown();
        }
        return prom
            .then(() => {
                this._activeView = undefined;
                return this._renderer.shutdown();
            })
            .then(() => {
                //DI is expected to be cleaned up by parent.
                this._di = undefined;
                this._logger = undefined;
                this._registeredViews = undefined;
                this._registeredActions = undefined;
                this._registeredViewActions = undefined;
                this._renderer = undefined;
                this._cabinetCmpnt = undefined;
                this._breadCrumbCmpnt = undefined;
                this._viewActionsCmpnt = undefined;
                this._viewContainerElem = undefined;
                this._titleCmpnt = undefined;
                this._storageFactory = undefined;
                this._solutionManager = undefined;
                this._gameEditorData = undefined;
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "GameEditor.shutdown() error");
            });
    }

    whenGameEditorStorageIsReady() {
        if (this._gameEditorData) {
            return Promise.resolve();
        } else {
            return this._storageFactory.whenStorageIsReady().then(() => {
                this._gameEditorData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "game_editor",
                    _.camelCase(this.currentSolutionName)
                );

                return Promise.resolve();
            });
        }
    }

    /******************/
    /*    CABINET     */
    /******************/
    showCabinetCpmnt() {
        this._cabinetCmpnt.show();
    }
    hideCabinetCpmnt() {
        this._cabinetCmpnt.hide();
    }

    /******************/
    /*    ACTIONS     */
    /******************/
    enableAction(regName) {
        this._cabinetCmpnt.enableAction(regName);
    }
    disableAction(regName) {
        this._cabinetCmpnt.disableAction(regName);
    }
    enableAllActions() {
        this._cabinetCmpnt.enableAllActions();
    }
    disableAllActions() {
        this._cabinetCmpnt.disableAllActions();
    }
    registerActions(actionsArr, silent) {
        this._registeredActions = actionsArr;
        this._cabinetCmpnt.setRegisteredActions(this._registeredActions, silent);
        this._logger.trace(this._registeredActions, "GameEditor.registerActions() result");
    }
    runAction(fqn) {
        let diOpts = _.cloneDeep(this._defaultDiOpts);
        diOpts.injectableOverrides = {
            "gameEditor.activeView": this._activeView,
            "gameEditor.gameEditor": this,
        };

        let action = this.createItem(fqn, diOpts);

        // Not every action's run() returns a promise, so normalize before chaining
        return Promise.resolve(action.run())
            .catch((err) => {
                this._logger.warn({error: err}, "GameEditor.runAction() error.");
            })
            .then(() => {
                if (action.shutdown) {
                    action.shutdown();
                }
                action = null;
            });
    }
    runUnregisteredAction(actionClass, argsArr) {
        try {
            let action = this._di.create(actionClass, this._defaultDiOpts);
            if (argsArr) {
                return action.run.apply(null, argsArr);
            } else {
                return action.run();
            }
        } catch (err) {
            this._logger.warn(err, "GameEditor.launchUnregisteredAction() error");
        }
    }

    /******************/
    /*     VIEWS      */
    /******************/
    _onSelectView(fqn) {
        let newState = _.merge({}, this.editorState, {fqn: fqn});
        this.setEditorState(newState);
    }
    registerViews(viewsArr, silent) {
        this._registeredViews = viewsArr;
        this._cabinetCmpnt.setRegisteredViews(this._registeredViews, silent);
        this._logger.trace(this._registeredViews, "GameEditor.registerViews() result.");
    }

    setActiveView(fqn, bcs, viewState) {
        if (this._activeRegistrationInProgress) {
            return Promise.reject({error: "existingView"});
        }
        let viewItem = this.getItem(fqn);
        if (viewItem) {
            //We are already working on switching to this view...
            if (_.get(this, "_activeRegistrationInProgress.fqn") === fqn) {
                return this._activeRegistrationInProgress.prom;
            } else {
                this._activeRegistrationInProgress = {
                    fqn: fqn,
                };

                this._activeRegistrationInProgress.prom = Promise.resolve()
                    .then(() => this._checkAndCallUnload())
                    .then(() => this._resetViewState())
                    .then(() => this.whenGameEditorStorageIsReady())
                    .then(() => {
                        //Build the View through DI.
                        let diOpts = _.cloneDeep(this._defaultDiOpts);
                        diOpts.injectableOverrides = {
                            "gameEditor.viewState": viewState || {},
                            "gameEditor.gameEditor": this,
                            "gameEditor.titleComponent": this._titleCmpnt,
                        };
                        this._activeView = this.createItem(fqn, diOpts);
                        this.setViewName(viewItem.registeredObj.$displayDetails.name);
                        this._activeRegistrationInProgress = undefined;

                        //Change the Title header to the display name provided by View.
                        this._titleCmpnt.title = viewItem.registeredObj.$displayDetails.name;
                        if (viewItem.registeredObj.$viewHeaderAction) {
                            this.setViewHeaderAction(viewItem.registeredObj.$viewHeaderAction);
                        }
                        //Set the breadcrumbs
                        this._breadCrumbCmpnt.setBreadcrumbs(bcs || []);

                        //Let the cabinet know which view has been selected
                        this._cabinetCmpnt.setSelectedView(fqn);

                        //Save current explorer view
                        //if (this.currentSolutionName === "config_drift" || this.currentSolutionName === "os_manager") {
                        //    let currentPath = fqn.split("."),
                        //        lastAction = currentPath[currentPath.length - 1];
                        //Prevent saving editor screen to state
                        //    if(lastAction === "Grid" || lastAction === "SwitchToDeviceExplorer" || lastAction === "SwitchToOsPlan") {
                        //        this._gameEditorData.setData(fqn, "currentExplorerView");
                        //    }
                        //}
                        //Append and render baby!
                        this._viewContainerElem.appendChild(this._activeView.renderer.rootElement);
                        return this._activeView.renderer.render();
                    })
                    .catch((err) => {
                        if (!err.userRejected) {
                            this._logger.warn({error: err}, "GameEditor.setActiveView() error.");
                        }
                        this._activeRegistrationInProgress = undefined;
                        throw err;
                    });
                return this._activeRegistrationInProgress.prom;
            }
        } else {
            this._logger.warn("GameEditor.setActiveView() - Unable to retrieve FQN: " + fqn);
            return Promise.reject();
        }
    }

    openLMRBExplorer(LMRBName) {
        return Promise.resolve()
            .then(() => {
                if (_.isString(LMRBName)) {
                    this._gameEditorData.setData(LMRBName, "currentExplorerView");
                    this._LMRBLayout.openPart(LMRBName);
                    return Promise.resolve();
                } else {
                    throw "LRMB type is not correct";
                }
            })
            .catch((err) => {
                if (!err.userRejected) {
                    this._logger.warn({error: err}, "GameEditor.openLMRBExplorer() error.");
                }
                return Promise.reject(err);
            });
    }

    getViewName() {
        return _.get(this, "_activeView.$teve.ui.viewName", null);
    }
    setViewName(newTitle) {
        _.set(this, "_activeView.$teve.ui.viewName", newTitle);
        return this;
    }
    /*****************************/
    /*    EDITOR-TAB-ACTION     */
    /*****************************/
    _launchTabAction(config) {
        let realConfig = _.assign(
            {
                iconType: "actionable-icon-gl",
                iconName: "common.info",
                iconTitle: "Description",
                type: "modal" || "view",
                //fqn:"",custom component FQN
            },
            config
        );

        switch (realConfig.type) {
            case "modal":
                if (!this._tabActionComponent) {
                    let diOpts = {
                        injectableOverrides: {
                            "gameEditor.activeView": this._activeView,
                            "gameEditor.viewState": this._editorState,
                            "gameEditor.gameEditor": this,
                        },
                    };
                    this._tabActionComponent = this.createItem(realConfig.fqn, diOpts);
                }
                this._modalUserFeedback.displayCustom(this._tabActionComponent, {
                    dontDestroy: true,
                });
                break;
            case "view":
                this.setActiveView(realConfig.fqn);
                break;
        }
    }
    _onEditorTabActionIconClicked(actionConfig) {
        this._launchTabAction(actionConfig);
    }

    /*****************************/
    /*    VIEW-HEADER-ACTION     */
    /*****************************/

    setViewHeaderAction(config) {
        let defaults = _.assign(
            {
                iconType: "actionable-icon-gl",
                iconName: "common.info",
                iconTitle: "Description",
                type: "modal" || "view",
                fqn: "", //custom component FQN
            },
            config
        );

        this._titleCmpnt.setActionIconType(defaults.iconType);
        this._titleCmpnt.setActionIconName(defaults.iconName);
        this._titleCmpnt.setActionIconTitle(defaults.iconTitle);
        this._titleCmpnt.showActionIcon = true;
        switch (defaults.type) {
            case "modal":
                this._launchViewHeaderAction = () => {
                    if (!this._viewHeaderActionComponent) {
                        let diOpts = {
                            injectableOverrides: {
                                "gameEditor.activeView": this._activeView,
                                "gameEditor.viewState": this._editorState,
                                "gameEditor.gameEditor": this,
                            },
                        };
                        this._viewHeaderActionComponent = this.createItem(defaults.fqn, diOpts);
                    }
                    this._modalUserFeedback.displayCustom(this._viewHeaderActionComponent, {
                        dontDestroy: true,
                    });
                };
                break;
            case "view":
                this._launchViewHeaderAction = () => {
                    this.setActiveView(defaults.fqn);
                };
                break;
        }
    }

    clearViewHeaderAction() {
        this._launchViewHeaderAction = null;
        this._titleCmpnt.showActionIcon = false;
        if (this._viewHeaderActionComponent) {
            return this._viewHeaderActionComponent.shutdown().then(() => {
                this._viewHeaderActionComponent = null;
            });
        } else {
            return Promise.resolve();
        }
    }

    _onViewHeaderActionIconClicked() {
        if (this._launchViewHeaderAction) {
            this._launchViewHeaderAction();
        }
    }
    /***********************/
    /*    VIEW-ACTIONS     */
    /***********************/
    registerViewActions(viewActionArr) {
        this._registeredViewActions = viewActionArr;
        this._viewActionsCmpnt.registeredViewActions = this._registeredViewActions;
    }
    clearViewActions() {
        this._registeredViewActions = [];
        return this._viewActionsCmpnt.clearViewActions();
    }
    addViewAction(vaInfo) {
        this._registeredViewActions.push(vaInfo);
        this._viewActionsCmpnt.addViewAction(vaInfo);
    }
    removeViewAction(name) {
        let vaObj = this._registeredViewActions.find((obj) => {
            return obj.name === name;
        });
        if (vaObj) {
            let index = this._registeredViewActions.indexOf(vaObj);
            let remArr = this._registeredViewActions.splice(index, 1);
            this._viewActionsCmpnt.removeViewAction(remArr[0]);
        }
    }
    enableViewAction(viewActionName) {
        this._viewActionsCmpnt.enableViewAction(viewActionName);
    }
    disableViewAction(viewActionName) {
        this._viewActionsCmpnt.disableViewAction(viewActionName);
    }
    showViewAction(viewActionName) {
        this._viewActionsCmpnt.showViewAction(viewActionName);
    }
    hideViewAction(viewActionName) {
        this._viewActionsCmpnt.hideViewAction(viewActionName);
    }

    /**
     * Hide action icon from task bar
     * @param {string} actionName
     */
    hideBarAction(actionName) {
        this._cabinetCmpnt.hideAction(actionName);
    }

    /**
     * Show action icon from task bar
     * @param {string} actionName
     */
    showBarAction(actionName) {
        this._cabinetCmpnt.showAction(actionName);
    }

    /**
     * Hide view icon from task bar
     * @param {string} viewName
     */
    hideBarView(viewName) {
        this._cabinetCmpnt.hideView(viewName);
    }

    /**
     * Adding new actions to the bar
     * @param {Array} newActions //// [{fullyQualifiedName: Registrations.DEVICE_EXPLORER_ACTION_UPDATE_CISCO, shown: true, icon: "devicemanager.api_update", title: "Update Support Data", order: 2}}]
     */
    addBarActions(newActions) {
        const currentViews = this._cabinetCmpnt.getRegisteredActions();
        newActions.forEach((newAction) => {
            currentViews.splice((newAction.order && newAction.order - 1) || 1, 0, newAction);
        });
        this._cabinetCmpnt.setRegisteredActions(currentViews);
    }

    /**
     * Adding new views to the bar
     * @param {Array} newViews //// [{fullyQualifiedName: Registrations.DIE_VIEW_EOX_BULLETIN, shown: true, icon: "devicemanager.cisco_eox", title: "EOX Bulletin", name: "EOX Bulletin", order: 2}]
     */
    addBarViews(newViews) {
        const currentViews = this._cabinetCmpnt.getRegisteredViews();
        newViews.forEach((newView) => {
            currentViews.splice((newView.order && newView.order - 1) || 1, 0, newView);
        });
        this._cabinetCmpnt.setRegisteredViews(currentViews);
    }

    runViewAction(name, args) {
        let viewActionDef = this._registeredViewActions.find((obj) => {
            return obj.name === name;
        });
        if (viewActionDef) {
            return viewActionDef.handler.apply(null, args);
        } else {
            this._logger.warn(
                "GameEditor.runViewAction() - Unable to find view action definition for " + name
            );
            return Promise.reject("Unable to find view definition for " + name);
        }
    }

    /******************/
    /*   BREADCRUMB   */
    /******************/
    setBreadCrumbs(bcs) {
        this._breadCrumbCmpnt.setBreadcrumbs(bcs);
    }

    getBreadCrumbs() {
        return this._breadCrumbCmpnt.getBreadCrumbs();
    }

    _onBreadCrumbContentClicked(bObj) {
        let fqn = _.get(bObj, "value.fqn");
        let breadCrumb = _.get(bObj, "value.breadCrumb");
        let viewState = _.get(bObj, "value.viewState");

        if (fqn) {
            this.setActiveView(fqn, breadCrumb, viewState);
        } else {
            this._logger.warn(
                "GameEditor._onBreadCrumbContentClicked error: No fully qualified name provided!"
            );
        }
    }

    /*******************/
    /*  MODAL HELPERS  */
    /*******************/
    runConfirmationActionWorkflow(confirmInfo, handler, notifInfo) {
        let mergedConfirmInfo = _.assign(
            {
                message: "Are you sure?",
                title: "Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
                dontHide: true,
            },
            confirmInfo
        );

        let mergedNotifInfo = _.assign(
            {
                message: "Action Successful",
                title: "Action Successful",
                confirmDetails: {
                    text: "Ok",
                    type: "text",
                },
                notDismissable: true,
            },
            notifInfo
        );

        return this._modalUserFeedback
            .displayConfirmation(mergedConfirmInfo.message, mergedConfirmInfo)
            .then(() => {
                return this._modalUserFeedback.displayLoading();
            })
            .then(() => {
                return handler.apply(null, null).catch((err) => {
                    this._logger.warn(err, ".runConfirmationActionWorkflow handler error.");
                    return this._modalUserFeedback.displayError(err);
                });
            })
            .then(() => {
                return this._modalUserFeedback.displayNotification(
                    mergedNotifInfo.message,
                    mergedNotifInfo
                );
            })
            .then(() => {
                return this._modalUserFeedback.hideDisplayed();
            })
            .catch((err) => {
                if (!err.userRejected) {
                    this._logger.debug(err, "GameEditor.runConfirmationActionWorkflow error");
                }
                return this._modalUserFeedback.hideDisplayed();
            });
    }

    runQuickConfirmationActionWorkflow(confirmInfo, confirmHandler, rejectHandler) {
        let mergedConfirmInfo = _.assign(
            {
                message: "Are you sure?",
                title: "Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
                dontHide: true,
            },
            confirmInfo
        );

        return this._modalUserFeedback
            .displayConfirmation(mergedConfirmInfo.message, mergedConfirmInfo)
            .then(() => {
                return this._modalUserFeedback.displayLoading();
            })
            .then(() => {
                return confirmHandler
                    .apply(null, null)
                    .then(() => {
                        return this._modalUserFeedback.hideDisplayed();
                    })
                    .catch((err) => {
                        this._logger.warn(
                            err,
                            ".runConfirmationActionWorkflow confirmHandler error."
                        );
                        return this._modalUserFeedback.displayError(err);
                    });
            })
            .catch((err) => {
                if (!err.userRejected) {
                    this._logger.warn(err, "GameEditor.runQuickConfirmationActionWorkflow error");
                }
                if (!_.isUndefined(rejectHandler)) {
                    return rejectHandler
                        .apply(null, null)
                        .then(() => {
                            return this._modalUserFeedback.hideDisplayed();
                        })
                        .catch((err) => {
                            this._logger.warn(
                                err,
                                ".runConfirmationActionWorkflow rejectHandler error."
                            );
                            return this._modalUserFeedback.displayError(err);
                        });
                } else {
                    return this._modalUserFeedback.hideDisplayed();
                }
            });
    }

    runActionNavigationWorkflow(handler, navInfo) {
        let mergedNavInfo = _.assign(
            {
                message: "Action Successful. Navigate to view?",
                title: "Confirm Navigation",
                confirmDetails: {
                    text: "Confirm",
                },
                fqn: "",
                breadcrumb: [],
                viewState: {},
            },
            navInfo
        );

        this._modalUserFeedback.displayLoading();

        return handler
            .apply(null, null)
            .catch((err) => {
                this._logger.warn(err, "GameEditor.runActionNavigationWorkflow handler error.");
                return this._modalUserFeedback.displayError(err);
            })
            .then(() => {
                return this._modalUserFeedback.displayConfirmation(
                    mergedNavInfo.message,
                    mergedNavInfo
                );
            })
            .then(() => {
                this.clearOnBeforeUnload();
                this._modalUserFeedback.hideDisplayed();
                return this.setActiveView(
                    mergedNavInfo.fqn,
                    mergedNavInfo.breadcrumb,
                    mergedNavInfo.viewState
                );
            })
            .catch((err) => {
                if (!err.userRejected) {
                    this._logger.warn(err, "GameEditor.runActionNavigationWorkflow() error.");
                }
                return this._modalUserFeedback.hideDisplayed();
            });
    }

    setOnBeforeUnload(arg) {
        if (_.isFunction(arg)) {
            this.onBeforeUnload = arg;
        } else if (_.isObject(arg)) {
            this.onBeforeUnload = () => {
                let mergedConfirmInfo = _.assign(
                    {
                        message: "Are you sure?",
                        title: "Confirmation Check",
                        confirmDetails: {
                            text: "Yes",
                            type: "text",
                        },
                    },
                    arg
                );
                return this._modalUserFeedback.displayConfirmation(
                    mergedConfirmInfo.message,
                    mergedConfirmInfo
                );
            };
        }
    }
    clearOnBeforeUnload() {
        this.onBeforeUnload = undefined;
    }

    _checkAndCallUnload() {
        if (!_.isFunction(this.onBeforeUnload)) {
            return Promise.resolve(false);
        } else {
            return this.onBeforeUnload();
        }
    }

    /**********************************/
    /* DEFAULT EMPTY MESSAGE WORKFLOW */
    /**********************************/
    showDefaultMessageView(opts, container) {
        if (this._defaultMessageViewCmpnt) {
            this._defaultMessageViewCmpnt.setMessage(opts.message);
            this._defaultMessageViewCmpnt.setIcon(opts.icon);
            this._defaultMessageViewCmpnt.setAction(opts.action);
            return this._defaultMessageViewCmpnt.renderer.render();
        } else {
            this._defaultMessageViewCmpnt = new DefaultMessageViewComponent({
                message: opts.message,
                icon: opts.icon,
                action: opts.action,
            });
            this._defaultMessageViewCmpnt.on(
                "SelectAction",
                (this.runAction = this.runAction.bind(this))
            );
            if (container) {
                container.appendChild(this._defaultMessageViewCmpnt.renderer.rootElement);
            } else {
                this._viewContainerElem.appendChild(
                    this._defaultMessageViewCmpnt.renderer.rootElement
                );
            }

            return this._defaultMessageViewCmpnt.renderer.render();
        }
    }

    hideDefaultMessageView() {
        if (this._defaultMessageViewCmpnt) {
            return new Promise((resolve, reject) => {
                this._defaultMessageViewCmpnt.removeListener("SelectAction", this.runAction);
                this._defaultMessageViewCmpnt
                    .shutdown()
                    .then(() => {
                        this._defaultMessageViewCmpnt = undefined;
                        resolve();
                    })
                    .catch((err) => {
                        reject(err);
                    });
            });
        } else {
            return Promise.resolve();
        }
    }

    /******************/
    /*  EDITOR STATE  */
    /******************/
    get editorState() {
        return this.getEditorState();
    }

    set editorState(val) {
        this.setEditorState(val);
    }

    getEditorState() {
        return this._editorState;
    }

    setEditorState(newState, force = false) {
        if (!_.isEmpty(newState) && _.isPlainObject(newState)) {
            let oldState = this._editorState;
            // this.emit('EditorStateChanged',this.editorState);    // didn't found if it is used somewhere, please remove
            if (
                _.get(oldState, "fqn", false) !== newState.fqn ||
                this._cabinetCmpnt._selectedView !== newState.fqn ||
                force
            ) {
                this.setActiveView(newState.fqn, newState.breadcrumb, newState.viewState)
                    .then(() => {
                        this._editorState = newState;
                    })
                    .catch((err) => {
                        console.warn(err);
                        if (err.error === "existingView") {
                            if (this._stateAttempt < MAX_ATTEMPTS) {
                                _.delay(() => {
                                    this._stateAttempt++;
                                    return this.setEditorState(newState, force, this._stateAttempt);
                                }, this._stateAttempt * 100);
                            } else {
                                console.error("Max attempts reached trying to open new state");
                            }
                        } else {
                            this._logger.warn({state: newState}, err);
                        }
                    });
            }
        } else {
            const errorMessage =
                "Attempting to set editor state with an empty object or a non-object.";
            if (this._logger) {
                this._logger.warn({state: newState}, errorMessage);
            } else {
                console.error(newState, errorMessage);
            }
        }
    }

    /**************************/
    /* RENDERER SETUP METHODS */
    /**************************/
    __setBreadCrumbComponent(cmpnt) {
        this._breadCrumbCmpnt = cmpnt;
    }
    __setViewContainerElement(elem) {
        this._viewContainerElem = elem;
    }
    __setTitleComponent(cmpnt) {
        this._titleCmpnt = cmpnt;
    }
    __setViewActionsComponent(cmpnt) {
        this._viewActionsCmpnt = cmpnt;
    }
    __setCabinetComponent(cmpnt) {
        this._cabinetCmpnt = cmpnt;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/
    _resetViewState() {
        this.enableAllActions();
        this.clearOnBeforeUnload();

        let promArr = [
            this.clearViewActions(),
            this.hideDefaultMessageView(),
            this.clearViewHeaderAction(),
        ];

        if (this._activeView) {
            promArr.push(this._activeView.shutdown());
        }
        return Promise.all(promArr)
            .then(() => {
                this._activeView = undefined;
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "GameEditor._resetViewState error");
            });
    }

    _triggerUserActionsPinned(eventData) {
        this.emit("UserActionsPinned", eventData);
    }

    _triggerUserViewsPinned(eventData) {
        this.emit("UserViewsPinned", eventData);
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    resetModel(silent) {
        let highcounter = 200;
        let ordersort = (a, b) => {
            if (!a.order) {
                a.order = highcounter;
                highcounter++;
            }

            if (!b.order) {
                b.order = highcounter;
                highcounter++;
            }

            if (a.order < b.order) {
                return -1;
            }
            if (a.order > b.order) {
                return 1;
            }
            return 0;
        };

        return this._resetViewState()
            .then(() => new Promise((resolve) => setTimeout(resolve, 0))) //make it more async. 'this.getAllItems()' call freezes UI for a about 0.5 - 1 sec
            .then(() => {
                let tbrActions = [];
                let tbrViews = [];
                let allParts = this.getAllItems();
                Object.keys(allParts).forEach((key) => {
                    Object.keys(allParts[key]).forEach((regName) => {
                        let regInfo = allParts[key][regName].registeredDetails;
                        let partInfo = allParts[key][regName].registeredObj.$displayDetails;
                        _.set(
                            partInfo,
                            "fullyQualifiedName",
                            regInfo.namespace + "." + regInfo.name
                        );
                        if (partInfo.type === "Action") {
                            tbrActions.push(partInfo);
                        } else if (partInfo.type === "View") {
                            tbrViews.push(partInfo);
                        }
                    });
                });
                this.registerActions(tbrActions.sort(ordersort), silent);
                this.registerViews(tbrViews.sort(ordersort), silent);
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "GameEditor.resetModel Error!");
                return Promise.reject(err);
            });
    }

    retardActions(retarded) {
        this.emit("RetardActions", retarded);
    }

    /**
     * Generate Hash string from input data using window.crypto and "SHA-256"
     * @param {*} stringToHash
     * @return {PromiseLike<string>}
     */
    generateHash(stringToHash) {
        const encoder = new TextEncoder();
        const data = encoder.encode(stringToHash);

        return window.crypto.subtle.digest("SHA-256", data).then(hexString);

        function hexString(buffer) {
            const byteArray = new Uint8Array(buffer);
            const hexCodes = [...byteArray].map((value) => {
                const hexCode = Number(value).toString(16);
                return hexCode.padStart(2, "0");
            });
            return hexCodes.join("");
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = GameEditorComponent;
